import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import { type PieChartProps } from "@cloudscape-design/components/pie-chart"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { type MouseEvent, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { WorkflowStage } from "@/generated/sdk"
import type { DTARecord } from "../../services/dtaApi"
import { STATUS_COLOR } from "../../components/shared/dtaDisplay"
import StatTile from "../../components/shared/StatTile"
import StatusDonut from "../../components/shared/StatusDonut"
import { dtaDetailsPath, vendorSubmitPath } from "../../components/shared/vendorRoutes"
import { ROUTES } from "../../utils/constants"
import { countByAction, formatAge, isStale, oldestPending, toStageSlices } from "./insights"
import "./InsightsPanel.css"

interface InsightsPanelProps {
  /**
   * All of the vendor's DTAs already loaded by `useVendorDtasList` (every stage, unfiltered).
   */
  allDtas: DTARecord[]
  loading: boolean
  /**
   * Applies a stage filter to the DTA list. Routed through the dashboard's single filter mutator so
   * clicking a tile also resets pagination (avoids landing on an out-of-range page).
   */
  onStatusSelect: (stage: WorkflowStage) => void
}

const statusHref = (stage: WorkflowStage) => `${ROUTES.VENDOR_DASHBOARD}?status=${stage}`

// Stages shown in the "by stage" donut, in workflow order. Zero slices are filtered out before
// rendering (StatusDonut expects callers to drop empty slices).
const DONUT_STAGES: readonly WorkflowStage[] = [
  WorkflowStage.Review,
  WorkflowStage.Approved,
  WorkflowStage.Active,
  WorkflowStage.Inactive,
]

/**
 * "What needs your attention" — the actionable, prioritized view above the vendor's DTA list (#716,
 * vendor-side counterpart to the DAE health metrics in #288). Everything here derives from the
 * already-loaded `allDtas`; no new network calls.
 *
 * Top row: donut chart of DTAs by stage + oldest-pending table. Bottom row: priority tiles (To sign
 * / To submit / In review) that filter the invitations list on click.
 */
const InsightsPanel = ({ allDtas, loading, onStatusSelect }: InsightsPanelProps) => {
  const navigate = useNavigate()

  const counts = useMemo(() => countByAction(allDtas), [allDtas])
  const pending = useMemo(() => oldestPending(allDtas), [allDtas])

  const donutData: PieChartProps.Datum[] = useMemo(
    () => toStageSlices(allDtas, DONUT_STAGES),
    [allDtas],
  )

  // Tiles keep a real href (for a11y / open-in-new-tab) but drive the filter through the dashboard's
  // mutator on click so pagination resets alongside the status change.
  const selectStatus = (stage: WorkflowStage) => (event: MouseEvent) => {
    event.preventDefault()
    onStatusSelect(stage)
  }

  const pendingActionPath = (row: DTARecord) =>
    row.workflow_stage === WorkflowStage.Active
      ? vendorSubmitPath(row.dta_id, row.version)
      : dtaDetailsPath(row.dta_id, row.version)

  const pendingColumns: TableProps.ColumnDefinition<DTARecord>[] = [
    { id: "study", header: "Study", cell: (row) => row.study_id, width: 180 },
    { id: "domain", header: "Domain", cell: (row) => row.domains[0] || "-", width: 180 },
    {
      id: "stage",
      header: "Stage",
      width: 100,
      cell: (row) => <Badge color={STATUS_COLOR[row.workflow_stage]}>{row.workflow_stage}</Badge>,
    },
    {
      id: "age",
      header: "Waiting",
      width: 90,
      cell: (row) =>
        isStale(row.last_modified_at) ? (
          <Box color="text-status-error" fontWeight="bold">
            {formatAge(row.last_modified_at)}
          </Box>
        ) : (
          formatAge(row.last_modified_at)
        ),
    },
    {
      id: "action",
      header: "Action",
      width: 140,
      minWidth: 120,
      cell: (row) => (
        <Button variant="inline-link" onClick={() => navigate(pendingActionPath(row))}>
          {row.workflow_stage === WorkflowStage.Active ? "Submit data" : "Review & sign"}
        </Button>
      ),
    },
  ]

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Your priorities, and what has been waiting on you longest"
        >
          What needs your attention
        </Header>
      }
    >
      <SpaceBetween size="l">
        <div className="insights-top-row">
          <Container header={<Header variant="h3">Your DTAs by stage</Header>}>
            <StatusDonut
              data={donutData}
              innerMetricValue={String(allDtas.length)}
              innerMetricDescription="DTAs"
              loading={loading}
              ariaLabel="Your DTAs by stage"
            />
          </Container>
          <Container header={<Header variant="h3">Pending your action the longest</Header>}>
            <Table
              variant="embedded"
              columnDefinitions={pendingColumns}
              items={pending}
              trackBy={(row) => row.dta_id}
              ariaLabels={{ tableLabel: "Pending your action the longest" }}
              loading={loading && pending.length === 0}
              loadingText="Loading DTAs..."
              empty={
                <Box textAlign="center" color="inherit">
                  Nothing is waiting on you right now.
                </Box>
              }
            />
          </Container>
        </div>

        <ColumnLayout columns={3} minColumnWidth={3}>
          <StatTile
            to={statusHref(WorkflowStage.Review)}
            onClick={selectStatus(WorkflowStage.Review)}
            iconName="status-warning"
            iconVariant="warning"
            value={counts.toSign}
            label="To sign"
          />
          <StatTile
            to={statusHref(WorkflowStage.Active)}
            onClick={selectStatus(WorkflowStage.Active)}
            iconName="upload"
            iconVariant="link"
            value={counts.toSubmit}
            label="To submit"
          />
          <StatTile
            to={statusHref(WorkflowStage.Approved)}
            onClick={selectStatus(WorkflowStage.Approved)}
            iconName="contact"
            iconVariant="link"
            value={counts.inReview}
            label="In review"
          />
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  )
}

export default InsightsPanel
